import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { PageWrap } from '../../Objects/PageWrap/PageWrap'
import { ReactLink } from '../Link/Link'
import { Paragraph } from '../Paragraph/Paragraph'
import uuidv4 from '../../utils/uuidv4'

/**
 * Bronson Footer component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-footer--footer-full.html
 *
 * @param {React.ReactNode} [children] - Any of the following: {@link FooterModuleBreadcrumbs} {@link FooterModuleNavColumns} {@link FooterModuleMetaNav} {@link FooterModuleFootnotes}.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The Footer component.
 * @constructor
 */
export function Footer({ children, testId, ...otherProps }) {
  return (
    <footer className="c-footer" data-testid={testId} {...otherProps}>
      {children}
    </footer>
  )
}

Footer.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.node]),
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}

/**
 * FooterModuleBreadcrumbs
 * @namespace FooterModule
 *
 * @param {Breadcrumb} [children] - Expects a {@link Breadcrumb} component.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The FooterModuleBreadcrumbs component.
 * @constructor
 */
export function FooterModuleBreadcrumbs({ children, testId, ...otherProps }) {
  return (
    <div className="c-footer__module c-footer__module--breadcrumb" data-testid={testId} {...otherProps}>
      <PageWrap>
        <div className="c-footer-breadcrumb">{children}</div>
      </PageWrap>
    </div>
  )
}

FooterModuleBreadcrumbs.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.node]), // Bronson template: '@bronson-breadcrumb'. Use {@link Breadcrumb} component.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}

Footer.ModuleBreadcrumbs = FooterModuleBreadcrumbs

/**
 * FooterModuleNavColumns
 * @namespace FooterModule
 *
 * @param {FooterNavColumn|FooterNavColumn[]} [children] - Expects a collection of {@link FooterNavColumn}.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The FooterModuleNavColumns component.
 * @constructor
 */
export function FooterModuleNavColumns({ children, testId, ...otherProps }) {
  return (
    <div className="c-footer__module c-footer__module--nav" data-testid={testId} {...otherProps}>
      <PageWrap>
        <nav className="c-footer-nav">
          <div className="c-footer-nav__columns">{children}</div>
        </nav>
      </PageWrap>
    </div>
  )
}

FooterModuleNavColumns.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.node, PropTypes.array]), // Bronson template: 'footer-nav-columns'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}

Footer.ModuleNavColumns = FooterModuleNavColumns

/**
 * Bronson FooterNavColumn component (nested).
 *
 * @param {FooterNavColumnItem|FooterNavColumnItem[]} [children] - A collection of {@link FooterNavColumnItem}.
 * @param {string} [title] - The column title.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @returns {JSX.Element} - The FooterNavColumn component.
 * @constructor
 */
export function FooterNavColumn({ children, title, testId }) {
  /**
   * Add state handling for footer accordion.
   */
  const [active, setActive] = useState(false)

  /**
   * Allocate unique identifiers for accessibility reasons.
   * @type {string|*}
   */
  const buttonUUID = useMemo(() => uuidv4('bron-footer-button-'), [])
  const panelUUID = useMemo(() => uuidv4('bron-footer-panel-'), [])

  const titleClassName = classNames('c-footer-nav__title', 'js-footer-accordion__title', {
    'is-active': active,
  })
  const panelClassName = classNames('c-footer-nav__list', 'js-footer-accordion__panel', 'js-is-ready', {
    'is-closed': !active,
  })
  return (
    <div className="c-footer-nav__column js-footer-accordion js-is-ready" data-testid={testId}>
      {/* The `onClick` is purposefully put on the `.js-footer-accordion__title` to align with Bronson. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div className={titleClassName} onClick={() => setActive(!active)}>
        <h4 className="c-footer-nav__title-label is-heading">{title}</h4>
        {/* The accordion trigger is hidden on larger viewports. */}
        <button
          className="c-footer-nav__title-label js-footer-accordion__title-label is-button"
          type="button"
          aria-expanded={active}
          aria-controls={panelUUID}
          id={buttonUUID}
        >
          {title}
        </button>
      </div>
      <ul id={panelUUID} aria-labelledby={buttonUUID} role="region" className={panelClassName}>
        {children}
      </ul>
    </div>
  )
}

FooterNavColumn.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.node, PropTypes.array]), // Bronson template: 'item'.
  title: PropTypes.string, // Bronson template: 'title'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}

Footer.NavColumn = FooterNavColumn

/**
 * Bronson FooterNavColumnItem component (nested).
 *
 * @param {React.ReactNode|React.ReactNodeArray} [children] - The link label or content.
 * @param {string} [href] - Optional `[href]` value.
 * @param {object} [linkProps] - Additional link props.
 * @param {boolean} [normalLink] - How to render the {@link ReactLink} inside.
 * @param {function} [onClick] - Optional `onClick` handler.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The FooterNavColumnItem component.
 * @constructor
 */
export function FooterNavColumnItem({ children, href, linkProps, normalLink, onClick, testId, ...otherProps }) {
  return (
    <li className="c-footer-nav__item" data-testid={testId} {...otherProps}>
      <ReactLink className="c-footer-nav__link" onClick={onClick} href={href} normalLink={normalLink} {...linkProps}>
        {children}
      </ReactLink>
    </li>
  )
}

FooterNavColumnItem.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.node, PropTypes.array]), // Bronson template: 'label'.
  href: PropTypes.string,
  linkProps: PropTypes.oneOfType([PropTypes.object]),
  normalLink: PropTypes.bool,
  onClick: PropTypes.func, // Added for `[onClick]` attribute.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}

Footer.NavColumn.Item = FooterNavColumnItem

/**
 * FooterModuleMetaNav
 * @namespace FooterModule
 *
 * @param {FooterModuleMetaNavItem|FooterModuleMetaNavItem[]} [children] - A collection of {@link FooterModuleMetaNavItem}.
 * @param {string} [copyrightSign='©'] - The signifier for the copyright sign.
 * @param {string} [copyrightText] - Additional copyright text.
 * @param {object[]} [socialItems] - A collection of social link items.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The FooterModuleMetaNav component.
 * @constructor
 */
export function FooterModuleMetaNav({
  children,
  copyrightSign = '©',
  copyrightText,
  socialItems,
  testId,
  ...otherProps
}) {
  return (
    <div className="c-footer__module c-footer__module--meta-nav" data-testid={testId} {...otherProps}>
      <div className="o-page-wrap">
        <nav className="c-footer-meta-bar">
          <ul className="c-footer-meta-nav">{children}</ul>
          {socialItems && (
            <ul className="c-footer-social-media">
              {socialItems.map(({ href, icon, label }, i) => {
                const iconClassNames = classNames('c-footer-social-media__icon', 'c-icon', `c-icon--[${icon}]`)
                return (
                  <li className="c-footer-social-media__item" key={i}>
                    <ReactLink href={href} title={label} className="c-footer-social-media__link">
                      <i className={iconClassNames} aria-hidden="true" role="img"></i>
                    </ReactLink>
                  </li>
                )
              })}
            </ul>
          )}
          <span className="c-footer-copyright">
            <span className="c-footer-copyright__sign">{copyrightSign}</span>
            <span className="c-footer-copyright__text">{copyrightText}</span>
          </span>
        </nav>
      </div>
    </div>
  )
}

FooterModuleMetaNav.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.node, PropTypes.array]), // Bronson template: 'footer-meta-nav-items'.
  copyrightSign: PropTypes.string, // Bronson template: 'footer-copyright.sign'.
  copyrightText: PropTypes.string, // Bronson template: 'footer-copyright.text'.
  socialItems: PropTypes.arrayOf(
    PropTypes.shape({ href: PropTypes.string, icon: PropTypes.string, label: PropTypes.string })
  ), // Bronson template: 'social-media-items'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}

Footer.ModuleMetaNav = FooterModuleMetaNav

/**
 * Bronson FooterModuleMetaNavItem component (nested).
 * @namespace FooterModule
 *
 * @param {React.ReactNode|React.ReactNodeArray} [children] - The link label or content.
 * @param {string} [href] - Optional `[href]` value.
 * @param {object} [linkProps] - Additional link props.
 * @param {boolean} [normalLink] - How to render the {@link ReactLink} inside.
 * @param {function} [onClick] - Optional `onClick` handler.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The FooterModuleMetaNavItem component.
 * @constructor
 */
export function FooterModuleMetaNavItem({ children, href, linkProps, normalLink, onClick, testId, ...otherProps }) {
  return (
    <li {...otherProps} className="c-footer-meta-nav__item" data-testid={testId}>
      <ReactLink
        className="c-footer-meta-nav__link"
        onClick={onClick}
        href={href}
        normalLink={normalLink}
        {...linkProps}
      >
        {children}
      </ReactLink>
    </li>
  )
}

FooterModuleMetaNavItem.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.node, PropTypes.array]), // Bronson template: 'label'.
  href: PropTypes.string,
  linkProps: PropTypes.oneOfType([PropTypes.object]),
  normalLink: PropTypes.bool,
  onClick: PropTypes.func, // Added for `[onClick]` attribute.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}

Footer.ModuleMetaNav.Item = FooterModuleMetaNavItem

/**
 * FooterModuleFootnotes
 * @namespace FooterModule
 *
 * @param {Footnote} [children] - Expects a {@link Footnote} component.
 * @param {React.ReactNode|string} [footerText] - Content for the footer text.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The FooterModuleFootnotes component.
 * @constructor
 */
export function FooterModuleFootnotes({ children, footerText, testId, ...otherProps }) {
  function renderFooterText() {
    if (footerText) {
      /**
       * If {@link footerText} is a string, just return it as a single child.
       */
      if (typeof footerText === 'string') {
        return <Paragraph>{footerText}</Paragraph>
      }
      return footerText
    }
    return null
  }

  return (
    <div className="c-footer__module c-footer__module--footnotes" data-testid={testId} {...otherProps}>
      <div className="o-page-wrap">
        <div className="c-footer-footnotes">
          {renderFooterText()}
          {children}
        </div>
      </div>
    </div>
  )
}

FooterModuleFootnotes.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.node]), // Bronson template: '@bronson-footnotes--footnotes-numbered'. Use {@link Footnote} component.
  footerText: PropTypes.oneOfType([PropTypes.element, PropTypes.node, PropTypes.string]),
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}

Footer.ModuleFootnotes = FooterModuleFootnotes
